import { ManagerBase, MouseEventResult } from './manager-base';
import { LevelDesignerComponent } from '../level-designer/level-designer.component';
import { LevelDesignerElementBase } from '../elements/element-base';
import { PolygonElement } from '../elements/polygon-element';
import { PointElement } from '../elements/point-element';

export class ScaleManager extends ManagerBase {
  private isScaling: boolean = false;
  private scaleHandle: string | null = null;
  private originalBounds: { x: number, y: number, width: number, height: number } | null = null;
  private originalElements: LevelDesignerElementBase[] = [];
  private scaleStartX: number = 0;
  private scaleStartY: number = 0;
  private handles: { [key: string]: { x: number, y: number, width: number, height: number } } = {};
  private isCtrlPressed: boolean = false;

  constructor(levelDesigner: LevelDesignerComponent, canvas: HTMLCanvasElement) {
    super(levelDesigner, canvas);
  }

  private calculateHandles(): void {
    const bounds = this.getSelectionBounds(true);
    // Increase bounds size by 5px
    const expandSize = 10 / this.levelDesigner.getScale();
    bounds.x -= expandSize;
    bounds.y -= expandSize;
    bounds.width += 2 * expandSize;
    bounds.height += 2 * expandSize;
    const handleSize = 10 / this.levelDesigner.getScale();
    const edgeThickness = 10 / this.levelDesigner.getScale();

    this.handles = {
      // Corner handles (square)
      'nw': { x: bounds.x - handleSize, y: bounds.y - handleSize, width: handleSize, height: handleSize },
      'ne': { x: bounds.x + bounds.width, y: bounds.y - handleSize, width: handleSize, height: handleSize },
      'sw': { x: bounds.x - handleSize, y: bounds.y + bounds.height, width: handleSize, height: handleSize },
      'se': { x: bounds.x + bounds.width, y: bounds.y + bounds.height, width: handleSize, height: handleSize },
      
      // Edge handles (long and thin)
      'n':  { x: bounds.x, y: bounds.y - edgeThickness, width: bounds.width, height: edgeThickness },
      's':  { x: bounds.x, y: bounds.y + bounds.height, width: bounds.width, height: edgeThickness },
      'w':  { x: bounds.x - edgeThickness, y: bounds.y, width: edgeThickness, height: bounds.height },
      'e':  { x: bounds.x + bounds.width, y: bounds.y, width: edgeThickness, height: bounds.height },
    };
  }

  public override handleMouseDown(event: MouseEvent): MouseEventResult | undefined {
    if (event.button === 1) return undefined;
    if (this.levelDesigner.selectedElements.length === 0) {
      return undefined; // Allow other managers to handle this event if no elements are selected
    }
    if (this.levelDesigner.selectedElements[0].isLocked()) {
      return undefined; // Allow other managers to handle this event if no elements are selected
    }
    const mousePos = this.getMousePosition(event);
    const handle = this.getScaleHandle(mousePos.x, mousePos.y);
    
    if (handle) {
      this.isScaling = true;
      this.scaleHandle = handle;
      this.originalBounds = this.getSelectionBounds(false);
      this.originalElements = this.levelDesigner.selectedElements.map(el => el.clone());
      this.scaleStartX = mousePos.x;
      this.scaleStartY = mousePos.y;
      return { cursor: this.getCursorForHandle(handle), stopPropagation: true, redraw: false };
    }
    
    return undefined;
  }
  private getSelectionBounds(expanded: boolean = true): { x: number, y: number, width: number, height: number } {
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;

    for (const element of this.levelDesigner.selectedElements) {
      const bounds = element.getBoundingBox();
      minX = Math.min(minX, bounds.x);
      minY = Math.min(minY, bounds.y);
      maxX = Math.max(maxX, bounds.x + bounds.width);
      maxY = Math.max(maxY, bounds.y + bounds.height);
    }

    return { x: minX, y: minY, width: maxX - minX, height: maxY - minY };
}


  public override handleMouseMove(event: MouseEvent): MouseEventResult | undefined {
    if(this.levelDesigner.selectedElements.length === 0) return undefined;
    if(this.levelDesigner.selectedElements[0].isLocked()) return undefined;
    if (this.isScaling) {
      const mousePos = this.getMousePosition(event);
      // Apply scaling to elements
      this.scaleSelection(mousePos.x, mousePos.y);

      this.levelDesigner.redraw();
      return { cursor: 'default', stopPropagation: true, redraw: true };
    }
    else{
        const mousePos = this.getMousePosition(event);
        const handle = this.getScaleHandle(mousePos.x, mousePos.y);
        
        if (handle) {
            return { cursor: this.getCursorForHandle(handle), stopPropagation: false, redraw: true };
        }
    }
    return undefined;
  }

  public override handleMouseUp(event: MouseEvent): MouseEventResult | undefined {
    if (this.isScaling) {
      this.isScaling = false;
      this.scaleHandle = null;
      this.originalBounds = null;
      this.originalElements = [];
      this.scaleStartX = 0;
      this.scaleStartY = 0;
      this.levelDesigner.saveState();
      return { cursor: 'default', stopPropagation: true, redraw: false };
    }
    return undefined;
  }

  public override OnDraw(): void {
    if (this.levelDesigner.selectedElements.length === 0) return;

    const ctx = this.canvas.getContext('2d');
    if (!ctx) return;

    this.calculateHandles();

    ctx.save();
    ctx.strokeStyle = 'blue';
    ctx.fillStyle = 'rgba(0, 0, 255, 0.2)';
    ctx.lineWidth = 2 / this.levelDesigner.getScale();

    // for (const handle of Object.values(this.handles)) {
    //   ctx.beginPath();
    //   ctx.rect(handle.x, handle.y, handle.width, handle.height);
    //   ctx.fill();
    //   ctx.stroke();
    // }

    ctx.restore();
  }

  private getScaleHandle(x: number, y: number): string | null {
    this.calculateHandles();

    for (const [handleName, handle] of Object.entries(this.handles)) {
      if (x >= handle.x && x <= handle.x + handle.width &&
          y >= handle.y && y <= handle.y + handle.height) {
        return handleName;
      }
    }

    return null;
  }

  private getCursorForHandle(handle: string): string {
    switch (handle) {
      case 'nw':
      case 'se':
        return 'nwse-resize';
      case 'ne':
      case 'sw':
        return 'nesw-resize';
      case 'n':
      case 's':
        return 'ns-resize';
      case 'e':
      case 'w':
        return 'ew-resize';
      default:
        return 'default';
    }
  }

  private scaleSelection(mouseX: number, mouseY: number): void {
    if (!this.originalBounds || !this.scaleHandle) return;

    const startTime = performance.now();

    const deltaX = mouseX - this.scaleStartX;
    const deltaY = mouseY - this.scaleStartY;

    let scaleX = 1, scaleY = 1;
    let anchorX = this.originalBounds.x, anchorY = this.originalBounds.y;

    switch (this.scaleHandle) {
      case 'nw':
        scaleX = (this.originalBounds.width - deltaX) / this.originalBounds.width;
        scaleY = (this.originalBounds.height - deltaY) / this.originalBounds.height;
        anchorX = this.originalBounds.x + this.originalBounds.width;
        anchorY = this.originalBounds.y + this.originalBounds.height;
        break;
      case 'n':
        scaleY = (this.originalBounds.height - deltaY) / this.originalBounds.height;
        anchorY = this.originalBounds.y + this.originalBounds.height;
        break;
      case 'ne':
        scaleX = (this.originalBounds.width + deltaX) / this.originalBounds.width;
        scaleY = (this.originalBounds.height - deltaY) / this.originalBounds.height;
        anchorY = this.originalBounds.y + this.originalBounds.height;
        break;
      case 'w':
        scaleX = (this.originalBounds.width - deltaX) / this.originalBounds.width;
        anchorX = this.originalBounds.x + this.originalBounds.width;
        break;
      case 'e':
        scaleX = (this.originalBounds.width + deltaX) / this.originalBounds.width;
        break;
      case 'sw':
        scaleX = (this.originalBounds.width - deltaX) / this.originalBounds.width;
        scaleY = (this.originalBounds.height + deltaY) / this.originalBounds.height;
        anchorX = this.originalBounds.x + this.originalBounds.width;
        break;
      case 's':
        scaleY = (this.originalBounds.height + deltaY) / this.originalBounds.height;
        break;
      case 'se':
        scaleX = (this.originalBounds.width + deltaX) / this.originalBounds.width;
        scaleY = (this.originalBounds.height + deltaY) / this.originalBounds.height;
        break;
    }

    // Prevent scaling to zero or negative
    scaleX = Math.max(0.01, scaleX);
    scaleY = Math.max(0.01, scaleY);

    if (this.isCtrlPressed && this.levelDesigner.selectedElements.length > 1) {
      // Scale positions while maintaining element sizes
      for (let i = 0; i < this.levelDesigner.selectedElements.length; i++) {
        const element = this.levelDesigner.selectedElements[i];
        const originalElement = this.originalElements[i];
        
        // Get the original center position relative to the anchor point
        const originalBounds = originalElement.getBoundingBox();
        const originalCenterX = originalBounds.x + originalBounds.width / 2;
        const originalCenterY = originalBounds.y + originalBounds.height / 2;
        
        // Calculate the new center position by scaling the distance from anchor
        const newCenterX = anchorX + (originalCenterX - anchorX) * scaleX;
        const newCenterY = anchorY + (originalCenterY - anchorY) * scaleY;
        
        // Calculate the required movement to place the element at the new center
        const currentBounds = element.getBoundingBox();
        const currentCenterX = currentBounds.x + currentBounds.width / 2;
        const currentCenterY = currentBounds.y + currentBounds.height / 2;
        
        const deltaX = newCenterX - currentCenterX;
        const deltaY = newCenterY - currentCenterY;
        
        // Move the element to the new position
        element.move(deltaX, deltaY);
      }
    } else {
      // Original scaling behavior
      for (let i = 0; i < this.levelDesigner.selectedElements.length; i++) {
        const element = this.levelDesigner.selectedElements[i];
        const originalElement = this.originalElements[i];
        if (element instanceof PolygonElement && originalElement instanceof PolygonElement) {
          element.scaleFrom(originalElement, scaleX, scaleY, anchorX, anchorY);
        } else if (element instanceof PointElement && originalElement instanceof PointElement) {
          const bounds = originalElement.getBoundingBox();
          const centerX = bounds.x + bounds.width / 2;
          const centerY = bounds.y + bounds.height / 2;
          const newX = anchorX + (centerX - anchorX) * scaleX;
          const newY = anchorY + (centerY - anchorY) * scaleY;
          element.moveTo(newX, newY);
        }
      }
    }

    const endTime = performance.now();
    console.log(`scaleSelection took ${endTime - startTime}ms`);
  }

  public override handleKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Control') {
      this.isCtrlPressed = true;
    }
  }

  public override handleKeyUp(event: KeyboardEvent): void {
    if (event.key === 'Control') {
      this.isCtrlPressed = false;
    }
  }
}
